import React, { useState, useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import arService from "../../../../../services/arService";
import { hasPermission } from "../../../../../utils/permission";
import LineItemsGrid from "../../../../common/LineItemsGrid";
import "./CreditNoteView.css";
import { showMessageBox } from '../../../../site/fn';

const CreditNoteView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creditNote, setCreditNote] = useState(null);
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const canEdit = hasPermission("ar-credit-note", "set");
  const canApprove = hasPermission("ar-credit-note", "approve");

  useEffect(() => {
    fetchCreditNote();
  }, [id]);

  const fetchCreditNote = async () => {
    try {
      setIsLoading(true);
      const data = await arService.getCreditNoteById(id);
      setCreditNote(data);
    } catch (error) {
      console.error("Error fetching credit note:", error);
      showMessageBox("Failed to fetch credit note details.", "error");
      navigate("/ar/credit-notes");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0.00";
    return `₹ ${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusBadge = (status, approvalStatus, isDraft) => {
    if (isDraft) {
      return <span className="status-badge draft">Draft</span>;
    }
    if (approvalStatus === "Approved") {
      return <span className="status-badge approved">Approved</span>;
    }
    if (approvalStatus === "Rejected") {
      return <span className="status-badge rejected">Rejected</span>;
    }
    if (approvalStatus === "Pending") {
      return <span className="status-badge pending">Pending Approval</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleApprove = async () => {
    if (!window.confirm(`Are you sure you want to approve Credit Note ${creditNote.doc_no}?`)) {
      return;
    }

    try {
      await arService.approveCreditNote(id);
      showMessageBox("Credit Note approved successfully!", "success");
      fetchCreditNote();
    } catch (error) {
      console.error("Error approving credit note:", error);
      const errorMessage = error.message || "Failed to approve credit note. Please try again.";
      showMessageBox(errorMessage, "error");
    }
  };

  const handleReject = async () => {
    const reason = window.prompt(`Enter reason for rejecting Credit Note ${creditNote.doc_no}:`);
    if (!reason) return;

    try {
      await arService.rejectCreditNote(id, reason);
      showMessageBox("Credit Note rejected successfully!", "success");
      fetchCreditNote();
    } catch (error) {
      console.error("Error rejecting credit note:", error);
      const errorMessage = error.message || "Failed to reject credit note. Please try again.";
      showMessageBox(errorMessage, "error");
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading credit note...</p>
      </div>
    );
  }

  if (!creditNote) {
    return (
      <div className="error-container">
        <p>Credit note not found.</p>
        <NavLink to="/ar/credit-notes">
          <button className="btn-back">Back to Credit Notes</button>
        </NavLink>
      </div>
    );
  }

  // If approved, redirect to print page to show PDF
  if (creditNote.approval_status === "Approved") {
    navigate(`/ar/credit-notes/${id}/print`, { replace: true });
    return null;
  }

  // Otherwise show form view with approval buttons
  return (
    <div className="credit-note-view-container">
      <div className="view-header no-print">
        <div className="header-left">
          <NavLink to="/ar/credit-notes">
            <button className="btn-back">← Back to List</button>
          </NavLink>
        </div>
        <div className="header-right">
          {canEdit && (creditNote.is_draft || creditNote.approval_status === "Rejected") && (
            <NavLink to={`/ar/credit-notes/${id}/edit`}>
              <button className="btn-edit">✏️ Edit</button>
            </NavLink>
          )}
          {canApprove &&
            (creditNote.approval_status === "Pending Approval" ||
              creditNote.approval_status === "Pending") && (
            <>
              <button className="btn-approve" onClick={handleApprove}>
                ✓ Approve
              </button>
              <button className="btn-reject" onClick={handleReject}>
                ✗ Reject
              </button>
            </>
          )}
        </div>
      </div>

      <div className="credit-note-document">
        {/* Document Header */}
        <div className="document-header">
          <h1>CREDIT NOTE</h1>
          <div className="doc-info">
            <div className="doc-number">
              <strong>Doc No:</strong> {creditNote.doc_no || "N/A"}
            </div>
            <div className="doc-status">{getStatusBadge(creditNote.status, creditNote.approval_status, creditNote.is_draft)}</div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="info-section">
          <h3>Customer & Project Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Credit Note Date:</label>
              <span>{formatDate(creditNote.credit_note_date)}</span>
            </div>
            <div className="info-item">
              <label>Customer:</label>
              <span>{creditNote.customer_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Project:</label>
              <span>{project?.project_name || creditNote.project_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>City:</label>
              <span>{creditNote.city || project?.city || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>State:</label>
              <span>{creditNote.state || project?.state_name || "N/A"}</span>
            </div>
          </div>
        </div>

        {/* Invoice & Order Details */}
        {(creditNote.original_invoice_no || creditNote.buyer_order_no) && (
          <div className="info-section">
            <h3>Invoice & Order Details</h3>
            <div className="info-grid">
              {creditNote.original_invoice_no && (
                <>
                  <div className="info-item">
                    <label>Original Invoice No:</label>
                    <span>{creditNote.original_invoice_no}</span>
                  </div>
                  <div className="info-item">
                    <label>Original Invoice Date:</label>
                    <span>{formatDate(creditNote.original_invoice_date)}</span>
                  </div>
                </>
              )}
              {creditNote.buyer_order_no && (
                <>
                  <div className="info-item">
                    <label>Buyer Order No:</label>
                    <span>{creditNote.buyer_order_no}</span>
                  </div>
                  <div className="info-item">
                    <label>Buyer Order Date:</label>
                    <span>{formatDate(creditNote.buyer_order_date)}</span>
                  </div>
                </>
              )}
              {creditNote.period_from && (
                <>
                  <div className="info-item">
                    <label>Period From:</label>
                    <span>{formatDate(creditNote.period_from)}</span>
                  </div>
                  <div className="info-item">
                    <label>Period To:</label>
                    <span>{formatDate(creditNote.period_to)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Reason */}
        <div className="info-section">
          <h3>Reason for Credit Note</h3>
          <p className="reason-text">{creditNote.reason || "N/A"}</p>
        </div>

        {/* Line Items */}
        {creditNote.line_items && creditNote.line_items.length > 0 && (
          <div className="info-section">
            <LineItemsGrid
              lineItems={creditNote.line_items}
              readOnly={true}
              variant="credit-note"
            />
          </div>
        )}

        {/* Amount Details */}
        <div className="info-section">
          <h3>Amount Details</h3>
          <div className="amount-table">
            <div className="amount-row">
              <span>Taxable Amount:</span>
              <strong>{formatCurrency(creditNote.taxable_amount)}</strong>
            </div>
            {parseFloat(creditNote.cgst_amount || 0) > 0 && (
              <div className="amount-row">
                <span>CGST @ {creditNote.cgst_percentage || 0}%:</span>
                <strong>{formatCurrency(creditNote.cgst_amount)}</strong>
              </div>
            )}
            {parseFloat(creditNote.sgst_amount || 0) > 0 && (
              <div className="amount-row">
                <span>SGST @ {creditNote.sgst_percentage || 0}%:</span>
                <strong>{formatCurrency(creditNote.sgst_amount)}</strong>
              </div>
            )}
            {parseFloat(creditNote.igst_amount || 0) > 0 && (
              <div className="amount-row">
                <span>IGST @ {creditNote.igst_percentage || 0}%:</span>
                <strong>{formatCurrency(creditNote.igst_amount)}</strong>
              </div>
            )}
            <div className="amount-row subtotal">
              <span>Total Tax:</span>
              <strong>{formatCurrency(
                (parseFloat(creditNote.cgst_amount || 0) + 
                 parseFloat(creditNote.sgst_amount || 0) + 
                 parseFloat(creditNote.igst_amount || 0))
              )}</strong>
            </div>
            <div className="amount-row total">
              <span>Grand Total:</span>
              <strong>{formatCurrency(creditNote.total_amount)}</strong>
            </div>
            {creditNote.amount_in_words && (
              <div className="amount-in-words">
                <em>Amount in Words: {creditNote.amount_in_words}</em>
              </div>
            )}
          </div>
        </div>

        {/* Additional Details */}
        {(creditNote.terms_of_payment || creditNote.dispatch_doc_no || creditNote.remarks) && (
          <div className="info-section">
            <h3>Additional Details</h3>
            <div className="info-grid">
              {creditNote.terms_of_payment && (
                <div className="info-item full-width">
                  <label>Terms of Payment:</label>
                  <span>{creditNote.terms_of_payment}</span>
                </div>
              )}
              {creditNote.dispatch_doc_no && (
                <div className="info-item">
                  <label>Dispatch Document No:</label>
                  <span>{creditNote.dispatch_doc_no}</span>
                </div>
              )}
              {creditNote.dispatch_through && (
                <div className="info-item">
                  <label>Dispatch Through:</label>
                  <span>{creditNote.dispatch_through}</span>
                </div>
              )}
              {creditNote.destination && (
                <div className="info-item">
                  <label>Destination:</label>
                  <span>{creditNote.destination}</span>
                </div>
              )}
              {creditNote.terms_of_delivery && (
                <div className="info-item">
                  <label>Terms of Delivery:</label>
                  <span>{creditNote.terms_of_delivery}</span>
                </div>
              )}
              {creditNote.remarks && (
                <div className="info-item full-width">
                  <label>Remarks:</label>
                  <span>{creditNote.remarks}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditNoteView;
